import _ from "lodash";
import { logDev, logEvent } from "@agtech/core/logging/AgTechLogger";
import { useMemo } from "react";
import { AgTechDataActionExecutor } from "@agtech/core/data/actions/AgTechDataActions";
import { AgTechGridItemGroup } from "../Features/AgTechGridGrouping";
import { IAgTechGridItem, AgTechGridColumnMetadata } from "./AgTechGridData";
import { AgTechGridEntity, getUpdatedEntitiesOnItemsChanged, getEntitiesAvailableBySearchState, getEntitiesAvailableByView, updateEntitiesOnEntityFocused, updateEntitiesOnEntitySelected, updateEntitiesOnEntityDeselected, updateEntitiesOnEntityRemoved, AgTechGridEntityStatus, updateEntitiesForNewDefaultEntity } from "./AgTechGridEntities";
import { AgTechGridConfiguration, AgTechGridEventHandlers, AgTechGridProps } from "./AgTechGridProps";
import { AgTechGridState, AgTechGridStateHandlingProps } from "./AgTechGridState";
import { AgTechWebAppContext } from "../../App/Contexts/AgTechWebAppContext";
import { DoSafely } from "@agtech/core/utilities/AgTechUtilities";

const GridRowHeight = 40;
const GridGroupHeaderHeight = 36;

export type AgTechGridItemIndex<TItem extends IAgTechGridItem> = {
    [index: string]: AgTechGridEntity<TItem> | AgTechGridItemGroup<TItem, any>
}

export type AgTechGridData<TItem extends IAgTechGridItem, TContextData = {}> = {
    itemIndex: AgTechGridItemIndex<TItem>,
    itemHeights: number[],
    columns: AgTechGridColumnMetadata<TItem, TContextData>[],
    configuration: AgTechGridConfiguration<TItem>,
    hasRowChanged: (prev: TItem, curr: TItem) => boolean,
    handlers: AgTechGridEventHandlers<TItem>,
    state: AgTechGridState<TItem, TContextData>,
    context: TContextData
}

export const useGridItemData = <TItem extends IAgTechGridItem, TContextData = {}>(props: {
    gridProps: AgTechGridProps<TItem, TContextData>,
    gridState: React.MutableRefObject<AgTechGridState<TItem, TContextData>>,
    updateGridState: (updatedState: AgTechGridState<TItem, TContextData>) => void,
    appContext: AgTechWebAppContext,
    actionExecutor: AgTechDataActionExecutor
}) => {
    return useMemo(() => {
        return buildGridItemData({
            gridProps: props.gridProps,
            stateProps: {
                ...props,
                gridState: () => props.gridState.current,
                updateGridEntities: updatedEntityState => {
                    props.updateGridState({
                        ...props.gridState.current,
                        entityState: {
                            ...updatedEntityState,
                            allEntities: { ...updatedEntityState.allEntities },
                            versionNumber: updatedEntityState.versionNumber + 1
                        }
                    });
                },
                updateGridEntity: (updatedEntityId, updatedEntity) => {
                    const entity = props.gridState.current.entityState.allEntities[updatedEntityId];

                    logEvent('Updated grid row entity', {
                        existing: entity,
                        updated: updatedEntity,
                    });
                    
                    props.gridState.current.entityState.allEntities[updatedEntityId] = {
                        ...entity,
                        ...updatedEntity,
                        versionNumber: entity.versionNumber + 1
                    }
                    
                    props.updateGridState({
                        ...props.gridState.current,
                        entityState: {
                            ...props.gridState.current.entityState,
                            versionNumber: props.gridState.current.entityState.versionNumber + 1,
                            allEntities: { ...props.gridState.current.entityState.allEntities }
                        }
                    });
                },
            }
        });
    },
    [
        props.gridProps.data.itemQuery.queryResult.data,
        props.gridState.current.entityState.versionNumber,
        props.gridState.current.searchState?.filter,
        props.gridState.current.selectedView
    ])
}

export const buildGridItemData = <TItem extends IAgTechGridItem, TContextData = {}>(props: {
    gridProps: AgTechGridProps<TItem, TContextData>,
    stateProps: AgTechGridStateHandlingProps<TItem, TContextData>
}) => {
    const gridState = props.stateProps.gridState();
    const eventHandlers = getAgTechGridEventHandlers(props.stateProps);

    const gridItemData: AgTechGridData<TItem, TContextData> = {
        itemIndex: {},
        itemHeights: [],
        columns: gridState.gridProps.columns,
        configuration: gridState.gridProps,
        hasRowChanged: (prev, curr) =>{
            return prev && curr && !gridState.gridProps.isRowEntityTheSame(prev, curr);
        },
        handlers: eventHandlers,
        state: gridState,
        context: props.gridProps.data.itemQuery.queryResult.data?.context ?? {} as any
    }

    DoSafely(() => {
        let availableEntityIndex = getUpdatedEntitiesOnItemsChanged(props.gridProps, gridState.entityState);

        // Include locally-created entities that haven't been written to the server yet
        // (they won't appear in gridData.items since they're not from the server)
        Object.entries(gridState.entityState.allEntities).forEach(([id, entity]) => {
            if (entity.state.wasCreated && !availableEntityIndex[id]) {
                availableEntityIndex[id] = entity;
            }
        });

        if (gridState.searchState?.filter) {
            availableEntityIndex = getEntitiesAvailableBySearchState(gridState);
        }

        if (gridState.selectedView) {
            availableEntityIndex = getEntitiesAvailableByView(gridState, availableEntityIndex);
        }

        let availableEntities = Object.values(availableEntityIndex);

        if (props.gridProps.sortingConfig) {
            availableEntities = _.sortBy(availableEntities, e => props.gridProps.sortingConfig?.getSortingKey(e.item));
        }

        if (gridState.gridProps.groupingConfig) {
            const groupingConfig = gridState.gridProps.groupingConfig!;
            const entityGroups = _.groupBy(availableEntities, entity => gridState.gridProps.groupingConfig?.group(entity.item));
            const entityGroupKeys = _.orderBy(Object.keys(entityGroups), key => key);

            let entityNumericIndex = 0;

            entityGroupKeys.forEach(key => {
                const entitiesInGroup = entityGroups[key];
                const groupText = groupingConfig.groupText?.(key, entitiesInGroup);
                const shouldShowGroupHeader = groupingConfig.showGroupHeader?.(key, entitiesInGroup, groupText);

                if (shouldShowGroupHeader) {
                    gridItemData.itemIndex[entityNumericIndex] = {
                        groupKey: key,
                        itemsInGroup: entitiesInGroup,
                        groupText: groupText,
                        groupHeader: gridState.gridProps.groupingConfig?.groupHeader
                    }
    
                    gridItemData.itemHeights.push(GridGroupHeaderHeight);
                }
                else {
                    entityNumericIndex = -1;
                }

                entitiesInGroup.forEach((entity, index) => {
                    entityNumericIndex++;

                    if (index === entitiesInGroup.length - 1) {
                        entity.attributes = {
                            isLastItemInGroup: true
                        };
                    }

                    gridItemData.itemIndex[entityNumericIndex] = entity;
                    gridItemData.itemHeights.push(GridRowHeight);
                });

                entityNumericIndex++;
            })
        }
        else {
            availableEntities.forEach((entity, index) => {
                gridItemData.itemIndex[index] = entity;
                gridItemData.itemHeights.push(38);
            });
        }
    });

    return gridItemData;
}

export const getAgTechGridEventHandlers = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>
): AgTechGridEventHandlers<TItem> => {
    return {
        onRowBlurred: async (blurredEntity: AgTechGridEntity<TItem>, originalItem: TItem) => {
            if (props.gridState().gridProps.editConfig) {
                await handleGridRowBlurred(props, blurredEntity, originalItem);
            }
        },
        onRowFocused: async (focusedEntity: AgTechGridEntity<TItem>) => {
            await updateEntitiesOnEntityFocused(props, focusedEntity);
        },
        onItemSelected: async (selectedEntity: AgTechGridEntity<TItem>) => {
            if (props.gridState().gridProps.selectionConfig) {
                await updateEntitiesOnEntitySelected(props, selectedEntity);
            }
        },
        onItemDeselected: async (deselectedEntity: AgTechGridEntity<TItem>) => {
            if (props.gridState().gridProps.selectionConfig) {
                await updateEntitiesOnEntityDeselected(props, deselectedEntity);
            }
        },
        onItemDeleted: async (deletedEntity: AgTechGridEntity<TItem>) => {
            let gridState = props.gridState();

            // If the entity was created but not written, it can be removed immediately
            if (deletedEntity.state.wasCreated && !deletedEntity.state.wasWritten) {
                await updateEntitiesOnEntityRemoved(props, deletedEntity);
            }
            else if (gridState.gridProps.editConfig && gridState.gridProps.editConfig.deleteItem) {
                let wasRowDeleted = await gridState.gridProps.editConfig.deleteItem({
                    actionExecutor: props.actionExecutor,
                    entity: deletedEntity
                });

                if (wasRowDeleted) {
                    await updateEntitiesOnEntityRemoved(props, deletedEntity);
                }
            }
        },
        onEnterKeyPressed: async (key: string, entity: AgTechGridEntity<TItem>, originalItem: TItem) => {
            let gridEditConfig = props.gridState().gridProps.editConfig;

            if (gridEditConfig && gridEditConfig.defaultItem && gridEditConfig.onEntityCreated) {
                await handleGridRowBlurred(props, entity, originalItem);

                if (entity.state.status !== AgTechGridEntityStatus.Invalid) {
                    await updateEntitiesForNewDefaultEntity(props);
                }
            }
        }
    }
}

const handleGridRowBlurred = async <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>,
    blurredEntity: AgTechGridEntity<TItem>,
    originalItem: TItem
) => {
    let gridEditConfig = props.gridState().gridProps.editConfig;

    if (!gridEditConfig) {
        return;
    }

    let hasEntityChanged = (blurredEntity.state.wasCreated && !blurredEntity.state.wasWritten) ||
        !props.gridState().gridProps.isRowEntityTheSame(originalItem, blurredEntity.item);

    if (hasEntityChanged) {
        logEvent('Grid row entity has changed', { entity: blurredEntity.id });

        // If the entity has been created but not written to the parent data source, write it
        // to the parent data source and mark it accordingly
        if (blurredEntity.state.wasCreated && !blurredEntity.state.wasWritten) {
            await HandleGridEntityCreation(props, blurredEntity);
        }
        else {
            await HandleGridEntityUpdating(props, blurredEntity, originalItem);
        }
    }
}

const HandleGridEntityCreation = async <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>,
    blurredEntity: AgTechGridEntity<TItem>
) => {
    let gridEditConfig = props.gridState().gridProps.editConfig;

    if (gridEditConfig && gridEditConfig.onEntityCreated) {
        logDev('Handling grid entity creation...');

        try
        {
            let entityCreationResponse = await gridEditConfig.onEntityCreated({
                actionExecutor: props.actionExecutor,
                entity: { ...blurredEntity }
            });

            if (entityCreationResponse.success) {      
                props.updateGridEntity(blurredEntity.id, {
                    ...blurredEntity,
                    item: { ...entityCreationResponse.success.data },
                    state: {
                        ...blurredEntity.state,
                        wasWritten: true,
                        status: AgTechGridEntityStatus.Created
                    }
                });
    
                logDev('Entity created with ID: ' + entityCreationResponse.success.data.id);
            }
            else {
                throw entityCreationResponse.errorMessage;
            }
        }
        catch (error)
        {
            console.error('An exception occurred creating an entity: ' + error);

            if (!blurredEntity.state.hasBeenWarned) {
                let gridState = props.gridState();

                let updateFailureMessage = gridState.gridProps.editConfig?.itemCreationError
                    ? gridState.gridProps.editConfig.itemCreationError(blurredEntity.item)
                    : 'An error occurred creating the item';

                props.appContext.messaging.showWarning({
                    header: updateFailureMessage,
                    details: error?.toString()
                });
            }

            props.updateGridEntity(blurredEntity.id, {
                ...blurredEntity,
                state: {
                    ...blurredEntity.state,
                    status: AgTechGridEntityStatus.Invalid,
                    hasBeenWarned: true
                }
            });
        }
    }
}

const HandleGridEntityUpdating = async <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>,
    blurredEntity: AgTechGridEntity<TItem>,
    originalItem: TItem
) => {
    let gridEditConfig = props.gridState().gridProps.editConfig;

    if (!gridEditConfig) {
        return;
    }

    let updateEntityResponse = await gridEditConfig.onEntityUpdated({
        actionExecutor: props.actionExecutor,
        entity: blurredEntity,
        originalItem: originalItem
    });

    if (updateEntityResponse.success) {
        if (updateEntityResponse.success.data) {
            props.updateGridEntity(blurredEntity.id, {
                ...blurredEntity,
                item: { ...updateEntityResponse.success.data },
                state: {
                    ...blurredEntity.state,
                    wasWritten: true,
                    status: blurredEntity.state.wasCreated ? AgTechGridEntityStatus.Created : AgTechGridEntityStatus.Updated
                }
            });
        }
        else {
            logDev('Entity update failed.');
        }
    }
    else {
        let wasUpdateCancelled = updateEntityResponse.wasCancelled ?? false;

        // No update was made so reset back to the prior row entity
        props.updateGridEntity(blurredEntity.id, {
            ...blurredEntity,
            item: { ...originalItem },
            state: {
                ...blurredEntity.state,
                status: wasUpdateCancelled ? blurredEntity.state.status : AgTechGridEntityStatus.Invalid
            }
        });

        if (!wasUpdateCancelled) {
            let gridState = props.gridState();

            let updateFailureMessage = gridState.gridProps.editConfig?.itemUpdateError
                ? gridState.gridProps.editConfig.itemUpdateError(blurredEntity.item)
                : 'An error occurred updating the item';
    
            props.appContext.messaging.showWarning({
                header: updateFailureMessage
            });
        }
    }
}